import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

// Attach JWT on every request
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Handle 401 globally → redirect to login
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// ── Convenience helpers ───────────────────────────────────────────────────────

export const authApi = {
  login:   (data) => api.post('/auth/login', data),
  me:      ()     => api.get('/auth/me'),
};

export const usersApi = {
  list:   ()       => api.get('/users'),
  create: (data)   => api.post('/users', data),
  update: (id, d)  => api.put(`/users/${id}`, d),
};

export const suppliersApi = {
  list:       ()       => api.get('/suppliers'),
  getOne:     (id)     => api.get(`/suppliers/${id}`),
  create:     (data)   => api.post('/suppliers', data),
  update:     (id, d)  => api.put(`/suppliers/${id}`, d),
  deactivate: (id)     => api.patch(`/suppliers/${id}/deactivate`),
};

export const brandsApi = {
  list:       ()       => api.get('/brands'),
  active:     ()       => api.get('/brands/active'),
  getOne:     (id)     => api.get(`/brands/${id}`),
  create:     (fd)     => api.post('/brands', fd),
  update:     (id, fd) => api.put(`/brands/${id}`, fd),
  deactivate: (id)     => api.patch(`/brands/${id}/deactivate`),
};

export const itemsApi = {
  list:       ()       => api.get('/items'),
  active:     ()       => api.get('/items/active'),
  getOne:     (id)     => api.get(`/items/${id}`),
  create:     (fd)     => api.post('/items', fd),
  update:     (id, fd) => api.put(`/items/${id}`, fd),
  deactivate: (id)     => api.patch(`/items/${id}/deactivate`),
};

export const poApi = {
  list:       ()     => api.get('/purchase-orders'),
  created:    ()     => api.get('/purchase-orders/created'),
  completed:  ()     => api.get('/purchase-orders/completed'),
  nextNumber: (supplierId, brandId) => api.get('/purchase-orders/next-number', { params: { supplier_id: supplierId, brand_id: brandId } }),
  exportExcel: () => api.get('/purchase-orders/export/excel', { responseType: 'blob' }),
  getOne:     (id)   => api.get(`/purchase-orders/${id}`),
  create:     (data) => api.post('/purchase-orders', data),
  update:     (id,d) => api.put(`/purchase-orders/${id}`, d),
  cancel:     (id)   => api.patch(`/purchase-orders/${id}/cancel`),
  pdf:        (id)   => `/api/purchase-orders/${id}/pdf`,
};

export const grnApi = {
  list:   ()     => api.get('/grn'),
  getOne: (id)   => api.get(`/grn/${id}`),
  create: (data) => api.post('/grn', data),
  update: (id,d) => api.put(`/grn/${id}`, d),
  cancel: (id)   => api.delete(`/grn/${id}`),
  exportExcel: () => api.get('/grn/export/excel', { responseType: 'blob' }),
};

export const purchaseReturnApi = {
  list:   ()     => api.get('/purchase-returns'),
  getOne: (id)   => api.get(`/purchase-returns/${id}`),
  create: (data) => api.post('/purchase-returns', data),
  update: (id,d) => api.put(`/purchase-returns/${id}`, d),
  exportExcel: () => api.get('/purchase-returns/export/excel', { responseType: 'blob' }),
};

export const invoicesApi = {
  list:           (params)     => api.get('/invoices', { params }),
  created:        ()           => api.get('/invoices/created'),
  delivered:      ()           => api.get('/invoices/delivered'),
  availableStock: (excludeId)  => api.get('/invoices/available-stock', { params: excludeId ? { exclude_inv_id: excludeId } : {} }),
  nextNumber:     ()           => api.get('/invoices/next-number'),
  customerStats:  (mobile)     => api.get('/invoices/customer-stats', { params: { mobile } }),
  exportExcel:    ()           => api.get('/invoices/export/excel', { responseType: 'blob' }),
  getOne:         (id)         => api.get(`/invoices/${id}`),
  create:         (data, opts) => api.post('/invoices', data, opts),
  update:         (id,d,opts)  => api.put(`/invoices/${id}`, d, opts),
  cancel:         (id)         => api.patch(`/invoices/${id}/cancel`),
  pdf:            (id)         => `/api/invoices/${id}/pdf`,
};

export const deliveriesApi = {
  list:       ()     => api.get('/deliveries'),
  getOne:     (id)   => api.get(`/deliveries/${id}`),
  create:     (data) => api.post('/deliveries', data),
  markReturn: (id)   => api.patch(`/deliveries/${id}/return`),
  exportExcel: () => api.get('/deliveries/export/excel', { responseType: 'blob' }),
};

export const salesReturnApi = {
  list:     ()     => api.get('/sales-returns'),
  listAll:  ()     => api.get('/sales-returns/all'),
  getOne:   (id)   => api.get(`/sales-returns/${id}`),
  create:   (data) => api.post('/sales-returns', data),
  update:   (id,d) => api.put(`/sales-returns/${id}`, d),
  exportExcel: () => api.get('/sales-returns/export/excel', { responseType: 'blob' }),
};

export const lookupApi = {
  statuses: () => api.get('/status'),
  couriers: () => api.get('/couriers'),
  colors:   () => api.get('/colors'),
  createStatus:  (d)   => api.post('/status', d),
  updateStatus:  (id,d)=> api.put(`/status/${id}`, d),
  deleteStatus:  (id)  => api.delete(`/status/${id}`),
  createCourier: (d)   => api.post('/couriers', d),
  updateCourier: (id,d)=> api.put(`/couriers/${id}`, d),
  deleteCourier: (id)  => api.delete(`/couriers/${id}`),
  createColor:   (d)   => api.post('/colors', d),
  updateColor:   (id,d)=> api.put(`/colors/${id}`, d),
  deleteColor:   (id)  => api.delete(`/colors/${id}`),
};

export const dashboardApi = {
  summary:       ()       => api.get('/dashboard/summary'),
  stock:         ()       => api.get('/dashboard/stock'),
  profit:        (from, to) => api.get('/dashboard/profit', { params: { from, to } }),
  monthlyProfit: (year)   => api.get('/dashboard/profit/monthly', { params: { year } }),
  monthlyDetail: (month)  => api.get(`/dashboard/profit/monthly/${month}`),
  invoiceStatus: (from, to) => api.get('/dashboard/invoice-status', { params: { from, to } }),
  search:        (params) => api.get('/dashboard/search', { params }),
  customersList:   (q, limit, from, to) => api.get('/dashboard/customers-list', { params: { q, limit, from, to } }),
  customersSearch: (q)      => api.get('/dashboard/customers-search', { params: { q } }),
  customerReport:  (mobile, from, to) => api.get('/dashboard/customer-report', { params: { mobile, from, to } }),
  exportReport:    (type, format, params = {}) =>
    api.get('/dashboard/export', { params: { type, format, ...params }, responseType: 'blob' }),
};